/**
 * @file CarrierMainFields.tsx
 * @description Основные параметры перевозчика (phase10.5.Y, вынесено в
 * phase19.12 из ShopCarrierConfigDialog при переезде настроек на страницу).
 *
 * Активность, приоритет, платёжные опции, ограничения веса и габаритов,
 * whitelist городов и блок кэша ПВЗ.
 */

import { CarrierCheckoutMapSelect } from "./CarrierCheckoutMapSelect";
import { CarrierDeliverySyncField } from "./CarrierDeliverySyncField";
import { CarrierPickupCacheCard } from "./CarrierPickupCacheCard";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import type {
  CarrierKind,
  CheckoutMapProvider,
  PickupSyncScope,
} from "../../lib/api-shop-carriers";

export interface MainFormState {
  isActive: boolean;
  priority: string;
  cashOnDelivery: boolean;
  onlinePayment: boolean;
  cityWhitelist: string;
  maxWeightG: string;
  maxDimensions: string;
  /** Область синхронизации кэша ПВЗ (phase19.12). */
  pickupSyncScope: PickupSyncScope;
  /** Период автообновления кэша ПВЗ, часы (null = выключено). */
  pickupSyncIntervalHours: number | null;
  /** Период автоопроса статусов доставки, минуты («0» = выключен). */
  deliverySyncIntervalMinutes: string;
  /** Чем рисуем карту ПВЗ на витрине. */
  checkoutMapProvider: CheckoutMapProvider;
}

interface Props {
  value: MainFormState;
  onChange: (patch: Partial<MainFormState>) => void;
  carrier: CarrierKind;
  /** Точек в кэше по данным конфига. */
  pickupPointCount: number;
  /** ISO даты последнего синка из конфига. */
  lastPickupSync: string | null;
}

export function CarrierMainFields({
  value,
  onChange,
  carrier,
  pickupPointCount,
  lastPickupSync,
}: Props) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex items-center justify-between gap-3 rounded-md border border-on-surface/10 p-3">
          <div>
            <Label htmlFor="active">Активна</Label>
            <p className="text-xs text-on-surface-variant">
              Покупателям видны варианты доставки этой ТК
            </p>
          </div>
          <Switch
            id="active"
            checked={value.isActive}
            onCheckedChange={(v) => onChange({ isActive: Boolean(v) })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="priority">Приоритет</Label>
          <Input
            id="priority"
            inputMode="numeric"
            value={value.priority}
            onChange={(e) => onChange({ priority: e.target.value })}
          />
          <p className="text-xs text-on-surface-variant">
            Меньше — выше в списке. Для автоматического выбора.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex items-center justify-between gap-3 rounded-md border border-on-surface/10 p-3">
          <div>
            <Label htmlFor="cod">Наложенный платёж</Label>
            <p className="text-xs text-on-surface-variant">Клиент оплачивает при получении</p>
          </div>
          <Switch
            id="cod"
            checked={value.cashOnDelivery}
            onCheckedChange={(v) => onChange({ cashOnDelivery: Boolean(v) })}
          />
        </div>
        <div className="flex items-center justify-between gap-3 rounded-md border border-on-surface/10 p-3">
          <div>
            <Label htmlFor="online">Онлайн-оплата</Label>
            <p className="text-xs text-on-surface-variant">Через эквайринг до отправки</p>
          </div>
          <Switch
            id="online"
            checked={value.onlinePayment}
            onCheckedChange={(v) => onChange({ onlinePayment: Boolean(v) })}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="maxWeight">Макс. вес (граммы)</Label>
          <Input
            id="maxWeight"
            inputMode="numeric"
            value={value.maxWeightG}
            placeholder="30000"
            onChange={(e) => onChange({ maxWeightG: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="maxDims">Макс. габариты (мм)</Label>
          <Input
            id="maxDims"
            value={value.maxDimensions}
            placeholder="600×400×400"
            onChange={(e) => onChange({ maxDimensions: e.target.value })}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cities">Города (whitelist)</Label>
        <Textarea
          id="cities"
          rows={3}
          value={value.cityWhitelist}
          placeholder="Москва, Челябинск, Санкт-Петербург (пусто = везде)"
          onChange={(e) => onChange({ cityWhitelist: e.target.value })}
        />
        <p className="text-xs text-on-surface-variant">
          Через запятую. Пусто — работает в любом городе РФ.
        </p>
      </div>

      {/* Карта витрины и кэш ПВЗ — только у СДЭК (карточка сама тянет журнал). */}
      {carrier === "SDEK" && (
        <CarrierCheckoutMapSelect
          value={value.checkoutMapProvider}
          onChange={(provider) => onChange({ checkoutMapProvider: provider })}
        />
      )}
      {/* Автоопрос статусов доставки — страховка на случай потери вебхука. */}
      <CarrierDeliverySyncField
        value={value.deliverySyncIntervalMinutes}
        onChange={(v) => onChange({ deliverySyncIntervalMinutes: v })}
      />

      {carrier === "SDEK" && (
        <CarrierPickupCacheCard
          carrier={carrier}
          pickupPointCount={pickupPointCount}
          lastPickupSync={lastPickupSync}
          scope={value.pickupSyncScope}
          intervalHours={value.pickupSyncIntervalHours}
          onChange={onChange}
        />
      )}
    </div>
  );
}
